// Mind2Magic CRM — Content Intelligence Module
// Scans CRM data for content-worthy signals and surfaces actionable ideas.
// Renders on-demand via showView('contenido') — not in renderAll().

import { TYPE_LABELS, STAGES } from './constants.js'
import { store } from './store.js'
import { FORMAT_TYPES, generateEnhancedDraft } from './content-templates.js'

// ========== CONTENT TRIGGERS ==========

const TRIGGER_TYPES = {
    booking: { label: 'Reserva confirmada', icon: '📦', color: '#22C55E' },
    quote: { label: 'Cotizacion enviada', icon: '📋', color: '#F97316' },
    trending: { label: 'Destino en tendencia', icon: '📈', color: '#A855F7' },
    countdown: { label: 'Cuenta regresiva', icon: '📅', color: '#3B82F6' },
    repeat: { label: 'Cliente frecuente', icon: '⭐', color: '#EAB308' },
    seasonal: { label: 'Oportunidad estacional', icon: '🌴', color: '#14B8A6' },
    milestone: { label: 'Logro del negocio', icon: '🏆', color: '#8B5CF6' },
    demand: { label: 'Demanda detectada', icon: '🔔', color: '#EC4899' },
    faq: { label: 'Pregunta frecuente', icon: '❓', color: '#06B6D4' },
}

// Stages where money has been committed (deposit paid or more)
const BOOKED_STAGES = ['reserva_abono', 'pago_completo', 'documentacion', 'en_destino', 'finalizado', 'postventa']
// Stages where a quote was sent but NO payment yet
const QUOTE_STAGES = ['cotizacion', 'revision_cotizacion', 'cotizacion_solicitada']

// ========== SCAN ENGINE ==========

function scanForIdeas() {
    const ideas = []
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const allLeads = [...store.leads, ...store.historico]
    const activeLeads = store.leads.filter(l => l.stage !== 'cancelado' && l.stage !== 'no_compro')

    // 1. Recent CONFIRMED bookings (deposit paid — NOT quotes)
    _scanRecentBookings(activeLeads, today, ideas)

    // 1b. Recent quotes (cotizacion sent — different content angle, NOT "just booked")
    _scanRecentQuotes(activeLeads, today, ideas)

    // 2. Trending destinations (3+ leads for same destination in last 14 days)
    _scanTrendingDestinations(activeLeads, today, ideas)

    // 3. Upcoming trips (countdown content, 7-30 days out)
    _scanUpcomingTrips(activeLeads, today, ideas)

    // 4. Repeat clients
    _scanRepeatClients(allLeads, ideas)

    // 5. Seasonal opportunities
    _scanSeasonalOpportunities(activeLeads, today, ideas)

    // 6. Business milestones
    _scanMilestones(allLeads, ideas)

    // 7. Demand signals (multiple inquiries for same type)
    _scanDemandSignals(activeLeads, today, ideas)

    // 8. FAQ from form submissions
    _scanFAQFromForms(today, ideas)

    // Sort by relevance: bookings first, then trending, then freshness
    ideas.sort((a, b) => {
        const pri = { booking: 0, countdown: 1, faq: 2, trending: 3, demand: 4, quote: 5, milestone: 6, repeat: 7, seasonal: 8 }
        return (pri[a.type] ?? 9) - (pri[b.type] ?? 9)
    })

    return ideas
}

function _scanRecentBookings(leads, today, ideas) {
    const sevenDaysAgo = new Date(today)
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

    const recent = leads.filter(l =>
        BOOKED_STAGES.includes(l.stage) &&
        l.updatedAt && new Date(l.updatedAt) >= sevenDaysAgo
    )

    for (const l of recent) {
        const dest = l.destination || TYPE_LABELS[l.type] || 'un viaje'
        const travelers = _describeTravelers(l)
        const isGroupTrip = _getTravelerCount(l) >= 6
        ideas.push({
            type: 'booking',
            title: `Reserva confirmada: ${dest}`,
            subtitle: travelers ? `${travelers}${isGroupTrip ? ' (viaje grupal)' : ''}` : '',
            lead: l,
            angles: [
                { format: 'caption', label: 'Post "Just Booked"' },
                { format: 'reel', label: 'Reel: Detras de escena' },
                ...(isGroupTrip ? [{ format: 'caption', label: 'Post: Viaje grupal' }] : []),
                { format: 'story', label: 'Story: Destino revelado' },
            ],
            dataContext: _buildContext(l),
        })
    }
}

function _scanRecentQuotes(leads, today, ideas) {
    const sevenDaysAgo = new Date(today)
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

    // Count quotes by destination — surface as "interest" not "booking"
    const quoteDests = {}
    for (const l of leads) {
        if (!QUOTE_STAGES.includes(l.stage)) continue
        if (!l.updatedAt || new Date(l.updatedAt) < sevenDaysAgo) continue
        const dest = _normalizeDestination(l.destination || TYPE_LABELS[l.type] || '')
        if (!dest) continue
        if (!quoteDests[dest]) quoteDests[dest] = { count: 0, types: new Set() }
        quoteDests[dest].count++
        if (l.type) quoteDests[dest].types.add(TYPE_LABELS[l.type] || l.type)
    }

    for (const [dest, data] of Object.entries(quoteDests)) {
        if (data.count >= 2) {
            ideas.push({
                type: 'quote',
                title: `${data.count} cotizaciones para ${dest}`,
                subtitle: `Interes activo (sin confirmar) — NO publicar como reserva`,
                angles: [
                    { format: 'caption', label: `Post: "Por que ${dest} esta de moda"` },
                    { format: 'reel', label: 'Reel: Tips para decidirse' },
                    { format: 'caption', label: 'Post: Que incluye una cotizacion' },
                ],
                dataContext: { destination: dest, quoteCount: data.count, types: [...data.types] },
            })
        }
    }
}

function _scanTrendingDestinations(leads, today, ideas) {
    const fourteenDaysAgo = new Date(today)
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14)

    // Count ONLY pre-booking leads — bookings are already confirmed, not "trending interest"
    const destCounts = {}
    for (const l of leads) {
        if (BOOKED_STAGES.includes(l.stage)) continue // already booked = not an inquiry
        const created = l.createdAt ? new Date(l.createdAt) : null
        if (!created || created < fourteenDaysAgo) continue
        const dest = _normalizeDestination(l.destination || TYPE_LABELS[l.type] || '')
        if (!dest) continue
        destCounts[dest] = (destCounts[dest] || 0) + 1
    }

    for (const [dest, count] of Object.entries(destCounts)) {
        if (count >= 3) {
            ideas.push({
                type: 'trending',
                title: `${dest} en tendencia`,
                subtitle: `${count} leads interesados en las ultimas 2 semanas (sin contar reservas)`,
                angles: [
                    { format: 'caption', label: `Post: "Por que todos preguntan por ${dest}"` },
                    { format: 'reel', label: `Reel: Top razones para ir a ${dest}` },
                    { format: 'caption', label: 'Post: Tips de planificacion' },
                ],
                dataContext: { destination: dest, inquiryCount: count },
            })
        }
    }
}

function _scanUpcomingTrips(leads, today, ideas) {
    for (const l of leads) {
        if (!BOOKED_STAGES.includes(l.stage) || !l.travelDate) continue
        const tripDate = new Date(l.travelDate)
        const daysUntil = Math.ceil((tripDate - today) / (1000 * 60 * 60 * 24))

        if (daysUntil >= 7 && daysUntil <= 30) {
            const dest = l.destination || TYPE_LABELS[l.type] || 'su viaje'
            ideas.push({
                type: 'countdown',
                title: `${daysUntil} dias para ${dest}`,
                subtitle: _describeTravelers(l) || 'Viaje confirmado',
                lead: l,
                angles: [
                    { format: 'story', label: `Story: "${daysUntil} dias para la magia"` },
                    { format: 'caption', label: 'Post: Checklist antes de viajar' },
                    { format: 'reel', label: 'Reel: Que empacar' },
                ],
                dataContext: _buildContext(l),
            })
        }
    }
}

function _scanRepeatClients(allLeads, ideas) {
    // Group leads by contacto (normalized)
    const clientTrips = {}
    for (const l of allLeads) {
        if (l.stage === 'cancelado' || l.stage === 'no_compro') continue
        const key = (l.contacto || l.titular || '').trim().toLowerCase()
        if (!key) continue
        if (!clientTrips[key]) clientTrips[key] = []
        clientTrips[key].push(l)
    }

    for (const [name, trips] of Object.entries(clientTrips)) {
        const booked = trips.filter(t => BOOKED_STAGES.includes(t.stage))
        if (booked.length >= 2) {
            const latest = booked.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0]
            ideas.push({
                type: 'repeat',
                title: `Cliente recurrente (${booked.length} viajes)`,
                subtitle: `Destinos: ${booked.map(b => _normalizeDestination(b.destination || TYPE_LABELS[b.type] || '')).filter(Boolean).join(', ') || 'Varios'}`,
                lead: latest,
                angles: [
                    { format: 'caption', label: 'Post: Historia de cliente fiel' },
                    { format: 'reel', label: 'Reel: "Cuando un cliente vuelve"' },
                    { format: 'caption', label: 'Post: Testimonio / recomendacion' },
                ],
                dataContext: { tripCount: booked.length },
            })
        }
    }
}

function _scanSeasonalOpportunities(leads, today, ideas) {
    const month = today.getMonth() // 0-indexed
    // Multiple seasonal hooks per month — first one that resonates
    const seasonalContent = {
        0: [
            { theme: 'Reservas de verano abren', tip: 'Dining reservations de Disney abren 60 dias antes — familias de junio ya deberian reservar' },
            { theme: 'Planificacion primer trimestre', tip: 'Enero es el mes con mas consultas — aprovechalo' },
        ],
        1: [
            { theme: 'Spring Break', tip: 'Familias viajan en marzo-abril. Si no reservaron, es el ultimo momento' },
            { theme: 'Cruceros de primavera', tip: 'Temporada de cruceros por el Caribe — precios suben en marzo' },
        ],
        2: [
            { theme: 'Semana Santa en los parques', tip: 'Semana Santa es temporada alta — clientes necesitan saber que esperar con las filas' },
            { theme: 'Spring Break ahora', tip: 'Clientes viajando esta semana — contenido de "en vivo" y tips de ultimo momento' },
            { theme: 'Vacaciones de junio', tip: 'Es el momento de reservar para vacaciones de junio-julio — los precios suben cada semana' },
        ],
        3: [
            { theme: 'Cruceros de verano', tip: 'Temporada alta de cruceros se acerca — cabinas se agotan' },
            { theme: 'Lightning Lane tips', tip: 'Familias de mayo-junio ya pueden reservar Lightning Lane — explicar como funciona' },
        ],
        4: [
            { theme: 'Graduaciones y celebraciones', tip: 'Familias celebrando graduaciones con viajes — angulo emocional fuerte' },
            { theme: 'Ultimo momento para verano', tip: 'Hoteles de julio-agosto casi llenos — urgencia real' },
        ],
        5: [
            { theme: 'Temporada alta', tip: 'Verano en los parques — contenido de supervivencia: calor, filas, hidratacion' },
            { theme: 'Halloween reservas', tip: 'Mickey\'s Not-So-Scary empieza en agosto — boletos ya a la venta' },
        ],
        6: [
            { theme: 'Halloween en Disney', tip: 'Eventos especiales de otono — contenido de que esperar y como prepararse' },
            { theme: 'Temporada baja = precios bajos', tip: 'Septiembre-octubre son meses baratos — promover viajes de otono' },
        ],
        7: [
            { theme: 'Viajes de fin de ano', tip: 'Reservas de noviembre-diciembre se llenan — actuar ya' },
            { theme: 'Regreso a clases', tip: 'Familias planificando vacaciones de diciembre — es el momento' },
        ],
        8: [
            { theme: 'Very Merry Christmas Party', tip: 'Evento exclusivo con boletos limitados — urgencia real' },
            { theme: 'Black Friday de viajes', tip: 'Ofertas de cruceros y paquetes — guia de que vale la pena' },
        ],
        9: [
            { theme: 'Black Friday / Cyber Monday', tip: 'Ofertas reales vs falsas — posicionarse como experta' },
            { theme: 'Planificacion 2027', tip: 'Precios del proximo ano salen en noviembre — early birds' },
        ],
        10: [
            { theme: 'Navidad en los parques', tip: 'Contenido de festividades — decoraciones, shows, comida especial' },
            { theme: 'Regalos de viaje', tip: '"Regala un viaje" — angulo para familias que buscan regalo especial' },
        ],
        11: [
            { theme: 'Resumen del ano', tip: 'Cuantas familias viajaron, destinos top — contenido de cierre' },
            { theme: 'Tendencias del proximo ano', tip: 'Nuevos parques, nuevos barcos, nuevas atracciones — generar anticipacion' },
        ],
    }

    const hooks = seasonalContent[month] || []
    for (const s of hooks) {
        ideas.push({
            type: 'seasonal',
            title: s.theme,
            subtitle: s.tip,
            angles: [
                { format: 'caption', label: `Post: ${s.theme}` },
                { format: 'reel', label: `Reel: Lo que necesitas saber` },
                { format: 'caption', label: 'Post: Errores comunes al reservar' },
            ],
            dataContext: { month: month + 1, theme: s.theme },
        })
    }
}

function _scanMilestones(allLeads, ideas) {
    const completed = allLeads.filter(l =>
        ((['finalizado', 'postventa'].includes(l.stage) || l.isHistorico) && l.stage !== 'cancelado' && l.stage !== 'no_compro' && !l.wasCanceled)
    )

    // Check round numbers
    const milestones = [10, 25, 50, 75, 100, 150, 200, 250, 300, 500]
    for (const m of milestones) {
        if (completed.length >= m && completed.length < m + 5) {
            ideas.push({
                type: 'milestone',
                title: `${m}+ viajes completados`,
                subtitle: 'Momento para celebrar y compartir',
                angles: [
                    { format: 'caption', label: `Post: "Ya son ${m} familias viajando con nosotros"` },
                    { format: 'reel', label: 'Reel: Resumen de logros' },
                    { format: 'story', label: 'Story: Agradecimiento' },
                ],
                dataContext: { completedCount: completed.length, milestone: m },
            })
            break
        }
    }
}

function _scanDemandSignals(leads, today, ideas) {
    const sevenDaysAgo = new Date(today)
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

    // Count ONLY pre-booking leads by trip type — exclude already-booked
    const typeCounts = {}
    for (const l of leads) {
        if (BOOKED_STAGES.includes(l.stage)) continue // already booked = not demand signal
        const created = l.createdAt ? new Date(l.createdAt) : null
        if (!created || created < sevenDaysAgo) continue
        const t = l.type || 'otro'
        typeCounts[t] = (typeCounts[t] || 0) + 1
    }

    for (const [type, count] of Object.entries(typeCounts)) {
        if (count >= 3 && type !== 'otro') {
            const label = TYPE_LABELS[type] || type
            ideas.push({
                type: 'demand',
                title: `Alta demanda: ${label}`,
                subtitle: `${count} leads interesados esta semana (sin contar reservas)`,
                angles: [
                    { format: 'caption', label: `Post: "Esta semana todos preguntan por ${label}"` },
                    { format: 'reel', label: `Reel: FAQ sobre ${label}` },
                    { format: 'caption', label: `Post: Mitos vs realidad de ${label}` },
                ],
                dataContext: { tripType: type, typeLabel: label, count },
            })
        }
    }
}

function _scanFAQFromForms(today, ideas) {
    const submissions = store.allFormSubmissions || []
    if (!submissions.length) return

    const fourteenDaysAgo = new Date(today)
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14)

    const recent = submissions.filter(s => s.created_at && new Date(s.created_at) >= fourteenDaysAgo)
    if (recent.length < 3) return

    // Analyze common trip types from form data
    const tripTypes = {}
    const destinations = {}
    for (const s of recent) {
        const fd = s.form_data || {}
        const tipo = fd.tipoViaje || fd.form_type || ''
        if (tipo) tripTypes[tipo] = (tripTypes[tipo] || 0) + 1
        const dest = fd.disneyDestino || fd.universalDestino || fd.destinosCrucero || ''
        if (dest) destinations[dest] = (destinations[dest] || 0) + 1
    }

    // Find the most asked-about topic
    const topType = Object.entries(tripTypes).sort((a, b) => b[1] - a[1])[0]
    const topDest = Object.entries(destinations).sort((a, b) => b[1] - a[1])[0]

    if (topType && topType[1] >= 3) {
        const label = topType[0]
        ideas.push({
            type: 'faq',
            title: `Pregunta frecuente: ${label}`,
            subtitle: `${topType[1]} formularios sobre ${label} en las ultimas 2 semanas`,
            angles: [
                { format: 'caption', label: `Post: "Lo que todos preguntan sobre ${label}"` },
                { format: 'reel', label: `Reel: FAQ de ${label}` },
                { format: 'caption', label: 'Post: Mitos y verdades' },
            ],
            dataContext: { topic: label, count: topType[1], totalForms: recent.length },
        })
    }

    if (topDest && topDest[1] >= 2 && (!topType || topDest[0] !== topType[0])) {
        ideas.push({
            type: 'faq',
            title: `Destino mas consultado: ${topDest[0]}`,
            subtitle: `${topDest[1]} formularios preguntando por este destino`,
            angles: [
                { format: 'caption', label: `Post: Guia rapida de ${topDest[0]}` },
                { format: 'reel', label: 'Reel: Lo basico que debes saber' },
            ],
            dataContext: { topic: topDest[0], count: topDest[1] },
        })
    }
}

// ========== DATA HELPERS ==========

function _getTravelerCount(lead) {
    const a = lead.formData?.adultos || 0
    const m = lead.formData?.menores || 0
    if (a + m > 0) return a + m
    // Try parsing viajeros string
    const v = lead.viajeros || ''
    const nums = v.match(/\d+/g)
    return nums ? nums.reduce((sum, n) => sum + parseInt(n, 10), 0) : 0
}

function _describeTravelers(lead) {
    if (lead.viajeros) return lead.viajeros
    const a = lead.formData?.adultos
    const m = lead.formData?.menores
    if (a || m) {
        const parts = []
        if (a) parts.push(`${a} adulto${a > 1 ? 's' : ''}`)
        if (m) parts.push(`${m} menor${m > 1 ? 'es' : ''}`)
        return parts.join(', ')
    }
    return ''
}

function _normalizeDestination(dest) {
    if (!dest) return ''
    const d = dest.toLowerCase()
    if (d.includes('disney world') || d.includes('walt disney')) return 'Disney World'
    if (d.includes('disneyland')) return 'Disneyland'
    if (d.includes('universal')) return 'Universal Orlando'
    if (d.includes('cruise') || d.includes('crucero')) return 'Crucero'
    return dest.split(',')[0].trim()
}

function _buildContext(lead) {
    return {
        destination: lead.destination || '',
        type: lead.type || '',
        typeLabel: TYPE_LABELS[lead.type] || '',
        travelers: _describeTravelers(lead),
        travelDate: lead.travelDate || '',
        hotel: lead.hotels?.[0]?.name || lead.formData?.hotelEspecifico || '',
        ship: lead.cruises?.[0]?.ship || '',
        cruiseLine: lead.cruises?.[0]?.line || '',
        parks: (lead.parkEntries || []).map(p => p.park).filter(Boolean),
        hasDining: lead.formData?.diningPlanType && lead.formData.diningPlanType !== 'none',
        celebration: lead.formData?.visionViaje || '',
    }
}

// ========== USADO TRACKING ==========

const USED_KEY = 'm2m_content_used'
let _hideUsedContent = false

function _getUsedSet() {
    try { return new Set(JSON.parse(localStorage.getItem(USED_KEY) || '[]')) }
    catch { return new Set() }
}

function _ideaKey(idea) {
    return `${idea.type}:${idea.title}`
}

function toggleUsado(ideaIdx) {
    const ideas = scanForIdeas()
    const idea = ideas[ideaIdx]
    if (!idea) return
    const key = _ideaKey(idea)
    const used = _getUsedSet()
    if (used.has(key)) used.delete(key)
    else used.add(key)
    localStorage.setItem(USED_KEY, JSON.stringify([...used]))
    renderContenido()
}

function isUsado(idea) {
    return _getUsedSet().has(_ideaKey(idea))
}

function toggleHideUsed() {
    _hideUsedContent = !_hideUsedContent
    renderContenido()
}

// ========== TRENDS CALCULATION ==========

function calculateTrends() {
    const leads = store.leads.filter(l => l.stage !== 'cancelado' && l.stage !== 'no_compro')
    const now = new Date()

    // Top destinations this month
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59)

    const thisMonthLeads = leads.filter(l => l.createdAt && new Date(l.createdAt) >= thisMonth)
    const lastMonthLeads = leads.filter(l => {
        const d = l.createdAt ? new Date(l.createdAt) : null
        return d && d >= lastMonth && d <= lastMonthEnd
    })

    // Destination ranking
    const destThis = _countBy(thisMonthLeads, l => _normalizeDestination(l.destination || TYPE_LABELS[l.type] || ''))
    const destLast = _countBy(lastMonthLeads, l => _normalizeDestination(l.destination || TYPE_LABELS[l.type] || ''))

    // Type ranking
    const typeThis = _countBy(thisMonthLeads, l => TYPE_LABELS[l.type] || l.type || 'Otro')
    const typeLast = _countBy(lastMonthLeads, l => TYPE_LABELS[l.type] || l.type || 'Otro')

    // Traveler composition
    const familySizes = thisMonthLeads.map(l => {
        const a = l.formData?.adultos || 0
        const m = l.formData?.menores || 0
        return a + m
    }).filter(n => n > 0)
    const avgFamilySize = familySizes.length ? (familySizes.reduce((a, b) => a + b, 0) / familySizes.length).toFixed(1) : '--'

    // Pipeline status breakdown for content context
    const bookedThisMonth = thisMonthLeads.filter(l => BOOKED_STAGES.includes(l.stage)).length
    const quotedThisMonth = thisMonthLeads.filter(l => QUOTE_STAGES.includes(l.stage)).length
    const inquiryThisMonth = thisMonthLeads.length - bookedThisMonth - quotedThisMonth

    return { destThis, destLast, typeThis, typeLast, avgFamilySize, thisMonthCount: thisMonthLeads.length, lastMonthCount: lastMonthLeads.length, bookedThisMonth, quotedThisMonth, inquiryThisMonth }
}

function _countBy(items, keyFn) {
    const counts = {}
    for (const item of items) {
        const k = keyFn(item)
        if (k) counts[k] = (counts[k] || 0) + 1
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1])
}

// ========== RENDER ==========

function renderContenido() {
    const container = document.getElementById('contenidoContent')
    if (!container) return

    const ideas = scanForIdeas()
    const trends = calculateTrends()

    const escHtml = window.escHtml || (s => s)

    // Filter used ideas if toggle is on
    const filteredIdeas = _hideUsedContent ? ideas.filter(idea => !isUsado(idea)) : ideas
    const usedCount = ideas.filter(idea => isUsado(idea)).length

    // Ideas section
    let ideasHTML = ''
    if (filteredIdeas.length === 0) {
        ideasHTML = `<div class="content-empty">
            <p>${_hideUsedContent && usedCount > 0 ? `${usedCount} ideas marcadas como usadas.` : 'No hay ideas de contenido en este momento.'}</p>
            <p style="color:var(--text-3);font-size:13px;">${_hideUsedContent ? 'Desactiva "Ocultar usados" para verlas.' : 'Las ideas aparecen automaticamente cuando hay reservas recientes, destinos en tendencia, o viajes proximos.'}</p>
        </div>`
    } else {
        ideasHTML = filteredIdeas.slice(0, 8).map((idea, i) => {
            const trigger = TRIGGER_TYPES[idea.type] || {}
            const used = isUsado(idea)
            const realIdx = ideas.indexOf(idea)
            return `<div class="content-idea-card" style="${used ? 'opacity:0.55;' : ''}">
                <div class="content-idea-header">
                    <span class="content-idea-badge" style="background:${trigger.color || '#6B4EFF'};">${trigger.icon || ''} ${escHtml(trigger.label || '')}</span>
                    <button class="content-usado-btn${used ? ' used' : ''}" onclick="toggleUsado(${realIdx})" title="${used ? 'Marcar como pendiente' : 'Marcar como usado'}">${used ? 'Usado' : 'Marcar usado'}</button>
                </div>
                <h3 class="content-idea-title">${escHtml(idea.title)}</h3>
                ${idea.subtitle ? `<p class="content-idea-subtitle">${escHtml(idea.subtitle)}</p>` : ''}
                <div class="content-idea-angles">
                    ${FORMAT_TYPES.map(f => `<button class="content-angle-btn" onclick="showContentDraft(${realIdx}, '${f.id}')">${f.label}</button>`).join('')}
                </div>
                <div class="content-draft-area" id="contentDraft_${realIdx}" style="display:none;"></div>
            </div>`
        }).join('')
    }

    // Trends section
    let trendsHTML = '<div class="content-trends-grid">'

    // Top destinations
    trendsHTML += `<div class="content-trend-card">
        <h4>Destinos del mes</h4>
        ${trends.destThis.length ? trends.destThis.slice(0, 5).map(([dest, count]) => {
            const lastCount = (trends.destLast.find(d => d[0] === dest) || [null, 0])[1]
            const delta = count - lastCount
            const arrow = delta > 0 ? `<span style="color:var(--green);">+${delta}</span>` : delta < 0 ? `<span style="color:var(--red);">${delta}</span>` : ''
            return `<div class="content-trend-row"><span>${escHtml(dest)}</span><span class="content-trend-count">${count} ${arrow}</span></div>`
        }).join('') : '<p class="content-trend-empty">Sin datos este mes</p>'}`
    trendsHTML += `</div>`

    // Top types
    trendsHTML += `<div class="content-trend-card">
        <h4>Tipo de viaje del mes</h4>
        ${trends.typeThis.length ? trends.typeThis.slice(0, 5).map(([type, count]) => {
            const lastCount = (trends.typeLast.find(t => t[0] === type) || [null, 0])[1]
            const delta = count - lastCount
            const arrow = delta > 0 ? `<span style="color:var(--green);">+${delta}</span>` : delta < 0 ? `<span style="color:var(--red);">${delta}</span>` : ''
            return `<div class="content-trend-row"><span>${escHtml(type)}</span><span class="content-trend-count">${count} ${arrow}</span></div>`
        }).join('') : '<p class="content-trend-empty">Sin datos este mes</p>'}`
    trendsHTML += `</div>`

    // Quick stats with pipeline breakdown
    trendsHTML += `<div class="content-trend-card">
        <h4>Este mes</h4>
        <div class="content-trend-row"><span>Total leads del mes</span><span class="content-trend-count">${trends.thisMonthCount}</span></div>
        <div class="content-trend-row"><span style="color:var(--green);">Reservas confirmadas</span><span class="content-trend-count" style="color:var(--green);">${trends.bookedThisMonth}</span></div>
        <div class="content-trend-row"><span style="color:var(--yellow);">Cotizaciones enviadas</span><span class="content-trend-count" style="color:var(--yellow);">${trends.quotedThisMonth}</span></div>
        <div class="content-trend-row"><span>En proceso</span><span class="content-trend-count">${trends.inquiryThisMonth}</span></div>
        <div class="content-trend-row" style="border-top:1px solid var(--border);padding-top:8px;margin-top:4px;"><span>Grupo promedio</span><span class="content-trend-count">${trends.avgFamilySize} personas</span></div>
        <div class="content-trend-row"><span>Mes pasado</span><span class="content-trend-count">${trends.lastMonthCount}</span></div>
    </div>`

    trendsHTML += '</div>'

    container.innerHTML = `
        <div class="content-section">
            <div class="content-section-header">
                <h2>Ideas para Hoy</h2>
                <div style="display:flex;gap:8px;align-items:center;">
                    ${usedCount > 0 ? `<label style="font-size:12px;color:var(--text-3);cursor:pointer;display:flex;align-items:center;gap:4px;">
                        <input type="checkbox" ${_hideUsedContent ? 'checked' : ''} onchange="toggleHideUsed()" style="cursor:pointer;">
                        Ocultar usados (${usedCount})
                    </label>` : ''}
                    <button class="btn btn-sm btn-secondary" onclick="renderContenido()">Actualizar</button>
                </div>
            </div>
            <div class="content-ideas-grid">${ideasHTML}</div>
        </div>
        <div class="content-section" style="margin-top:24px;">
            <div class="content-section-header">
                <h2>Tendencias</h2>
            </div>
            ${trendsHTML}
        </div>
    `
}

function showContentDraft(ideaIdx, format) {
    const draftArea = document.getElementById(`contentDraft_${ideaIdx}`)
    if (!draftArea) return

    // Toggle visibility
    if (draftArea.style.display !== 'none' && draftArea.dataset.format === format) {
        draftArea.style.display = 'none'
        return
    }

    const ideas = scanForIdeas()
    const idea = ideas[ideaIdx]
    if (!idea) return

    const draft = generateEnhancedDraft(idea, format)
    const escHtml = window.escHtml || (s => s)
    const formatLabel = FORMAT_TYPES.find(f => f.id === format)?.label || format

    draftArea.dataset.format = format
    draftArea.style.display = 'block'
    draftArea.innerHTML = `
        <div class="content-draft-box">
            <div class="content-draft-toolbar">
                <span class="content-draft-label">${escHtml(formatLabel)}</span>
                <div style="display:flex;gap:6px;">
                    <button class="btn btn-sm btn-secondary" onclick="showContentDraft(${ideaIdx}, '${format}')" title="Generar otra variacion">Otra</button>
                    <button class="btn btn-sm btn-secondary" onclick="copyContentDraft(${ideaIdx})" title="Copiar al portapapeles">Copiar</button>
                </div>
            </div>
            <pre class="content-draft-text" id="contentDraftText_${ideaIdx}">${escHtml(draft)}</pre>
        </div>
    `
}

function copyContentDraft(ideaIdx) {
    const el = document.getElementById(`contentDraftText_${ideaIdx}`)
    if (!el) return
    navigator.clipboard.writeText(el.textContent).then(() => {
        window.toast?.('Copiado al portapapeles', 'success')
    }).catch(() => {
        // Fallback
        const range = document.createRange()
        range.selectNodeContents(el)
        const sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(range)
        document.execCommand('copy')
        sel.removeAllRanges()
        window.toast?.('Copiado al portapapeles', 'success')
    })
}

// ========== WINDOW BRIDGE ==========
Object.assign(window, {
    renderContenido,
    showContentDraft,
    copyContentDraft,
    toggleUsado,
    toggleHideUsed,
})
